import { useState } from 'react'
import { isSameDay } from 'date-fns'
import { useTranslate } from '@ur-solutions/react-hooks'
import { FAIcon } from '@/components/fa-icon'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { format } from '@/utils/date-fns'
import type {
  WorkPeriodAddon,
  WorkPeriodAddonKind,
} from './use-work-period-addons'
import { useWorkPeriodAddons } from './use-work-period-addons'

const KIND_BADGE: Record<WorkPeriodAddonKind, string> = {
  time: 'border-sky-300 bg-sky-100 text-sky-900',
  fixed: 'border-violet-300 bg-violet-100 text-violet-900',
  override: 'border-amber-300 bg-amber-100 text-amber-900',
}

const KIND_ORDER: WorkPeriodAddonKind[] = ['time', 'fixed', 'override']

interface Props {
  userId: string
  datetimeStart: Date
  datetimeEnd: Date
  /**
   * The work period's server-rendered summary. Stands in for the chips until
   * the popover has been opened once and the real rows are in the cache.
   */
  addonsAsText: string[]
  canEdit: boolean
  /** Shown instead of the add actions when the day is locked. */
  lockedLabel?: string
  /** Lets the trigger fill a card instead of sitting inside a table cell. */
  fullWidth?: boolean
  onChange?: () => void
}

/**
 * The day's addons as chips that open an editor. Everything an approver can do
 * with a tillegg — see the whole list, change one, delete one, add another —
 * happens in the popover, so the column itself stays one line tall.
 */
export function WorkPeriodAddons({
  userId,
  datetimeStart,
  datetimeEnd,
  addonsAsText,
  canEdit,
  lockedLabel,
  fullWidth,
  onChange,
}: Props) {
  const translations = useTranslate({
    addons: 'salary.addons',
    addAddon: 'salary.add-addon',
    noAddons: 'salary.no-addons-registered',
    edit: 'common.edit',
    delete: 'common.delete',
    kind: {
      time: 'salary.time-addons',
      fixed: 'salary.fixed-addons',
      override: 'salary.salary-code-overrides',
    },
  })

  const [open, setOpen] = useState(false)
  const [everOpened, setEverOpened] = useState(false)

  const { addons, loading, loaded, can, add, edit, remove } =
    useWorkPeriodAddons({
      userId,
      datetimeStart,
      datetimeEnd,
      enabled: everOpened,
      onChange,
    })

  const labels = loaded
    ? addons.map(addon =>
        [addon.salaryCodeName, addon.value].filter(Boolean).join(' ')
      )
    : addonsAsText.filter(text => text.trim().length > 0)

  const addableKinds = KIND_ORDER.filter(kind => can[kind].add)
  const canAdd = canEdit && addableKinds.length > 0

  // The prompts and the confirm dialog render below the popover, so it has to
  // step aside before any of them open.
  function withPopoverClosed(action: () => Promise<void>) {
    return () => {
      setOpen(false)
      void action()
    }
  }

  function handleOpenChange(next: boolean) {
    if (next) setEverOpened(true)
    setOpen(next)
  }

  const dateLabel = isSameDay(datetimeStart, datetimeEnd)
    ? format(datetimeStart, 'EEE dd. MMM')
    : `${format(datetimeStart, 'dd. MMM')} – ${format(datetimeEnd, 'dd. MMM')}`

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={event => event.stopPropagation()}
          className={cn(
            'flex min-h-8 items-center gap-1 rounded-lg px-1.5 py-1 text-left',
            'cursor-pointer hover:bg-stone-100',
            fullWidth ? 'w-full' : 'max-w-full'
          )}
        >
          {labels.length === 0 ? (
            canAdd ? (
              <span className="inline-flex items-center gap-1.5 text-sm text-stone-500">
                <FAIcon icon="plus" />
                {translations.addAddon}
              </span>
            ) : (
              <span className="text-sm text-stone-400">–</span>
            )
          ) : (
            <>
              <span className="truncate rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-700">
                {labels[0]}
              </span>
              {labels.length > 1 && (
                <span className="shrink-0 rounded-full bg-stone-200 px-1.5 py-0.5 text-xs font-semibold text-stone-700">
                  +{labels.length - 1}
                </span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-88 p-0"
        onClick={event => event.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-2 border-b border-stone-200 px-4 py-3">
          <div>
            <div className="text-xs font-semibold tracking-[0.14em] uppercase text-stone-500">
              {translations.addons}
            </div>
            <div className="text-sm font-medium text-stone-900">
              {dateLabel}
            </div>
          </div>
          {loading && <Spinner className="size-4" />}
        </header>

        {addons.length === 0 ? (
          <p className="px-4 py-4 text-sm text-stone-500">
            {!loaded && loading ? '' : translations.noAddons}
          </p>
        ) : (
          <ul className="max-h-72 divide-y divide-stone-200 overflow-y-auto">
            {addons.map(addon => (
              <AddonRow
                key={`${addon.kind}-${addon.id}`}
                addon={addon}
                kindLabel={translations.kind[addon.kind]}
                editLabel={translations.edit}
                deleteLabel={translations.delete}
                canChange={canEdit && can[addon.kind].change}
                canDelete={canEdit && can[addon.kind].delete}
                onEdit={withPopoverClosed(() => edit(addon))}
                onDelete={withPopoverClosed(() => remove(addon))}
              />
            ))}
          </ul>
        )}

        <footer className="border-t border-stone-200 px-4 py-3">
          {canAdd ? (
            <div className="flex flex-wrap gap-2">
              {addableKinds.map(kind => (
                <Button
                  key={kind}
                  size="sm"
                  variant="outline"
                  onClick={withPopoverClosed(() => add(kind))}
                >
                  <FAIcon icon="plus" />
                  {translations.kind[kind]}
                </Button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-stone-500">{lockedLabel}</p>
          )}
        </footer>
      </PopoverContent>
    </Popover>
  )
}

interface AddonRowProps {
  addon: WorkPeriodAddon
  kindLabel: string
  editLabel: string
  deleteLabel: string
  canChange: boolean
  canDelete: boolean
  onEdit: () => void
  onDelete: () => void
}

function AddonRow({
  addon,
  kindLabel,
  editLabel,
  deleteLabel,
  canChange,
  canDelete,
  onEdit,
  onDelete,
}: AddonRowProps) {
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <div className="min-w-0 flex-1 space-y-1">
        <span
          className={cn(
            'inline-flex rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold tracking-wide uppercase',
            KIND_BADGE[addon.kind]
          )}
        >
          {kindLabel}
        </span>
        <div className="text-sm font-medium break-words text-stone-900">
          {addon.salaryCodeName}
        </div>
        <div className="text-sm text-stone-600">{addon.value}</div>
        {addon.comment && (
          <div className="text-xs break-words text-stone-500">
            {addon.comment}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {canChange && (
          <Button
            size="icon-sm"
            variant="ghost"
            title={editLabel}
            aria-label={editLabel}
            onClick={onEdit}
          >
            <FAIcon icon="pen-to-square" />
          </Button>
        )}
        {canDelete && (
          <Button
            size="icon-sm"
            variant="ghost"
            title={deleteLabel}
            aria-label={deleteLabel}
            className="text-red-600 hover:bg-red-50"
            onClick={onDelete}
          >
            <FAIcon icon="trash" />
          </Button>
        )}
      </div>
    </li>
  )
}
